import { Field } from '@/domain/trading'
import type { ChainId } from '@/sdk'

const STORAGE_KEY = 'nm.swapFormDraft'
const MAX_AGE_MS = 86_400_000

export type SwapFormDraft = {
  chainId: number
  inputCurrencyId: string
  outputCurrencyId: string
  independentField: Field
  typedValue: string
  updatedAt: number
}

function readRaw(): SwapFormDraft | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as SwapFormDraft
    if (!parsed?.chainId || !parsed.updatedAt) return null
    if (Date.now() - parsed.updatedAt > MAX_AGE_MS) {
      clearSwapFormDraft()
      return null
    }
    return parsed
  } catch {
    return null
  }
}

export function readSwapFormDraft(chainId: ChainId): SwapFormDraft | null {
  const draft = readRaw()
  if (!draft || draft.chainId !== chainId) return null
  return draft
}

export function persistSwapFormDraft(draft: Omit<SwapFormDraft, 'updatedAt'>): void {
  if (typeof window === 'undefined') return
  if (!draft.inputCurrencyId && !draft.outputCurrencyId && !draft.typedValue.trim()) return
  try {
    window.sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...draft, updatedAt: Date.now() } satisfies SwapFormDraft),
    )
  } catch {
    /* quota / private mode */
  }
}

export function clearSwapFormDraft(): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    /* ignore */
  }
}

export function hasSwapFormDraft(chainId?: number): boolean {
  const draft = readRaw()
  if (!draft) return false
  if (chainId !== undefined && draft.chainId !== chainId) return false
  return Boolean(draft.typedValue.trim() || (draft.inputCurrencyId && draft.outputCurrencyId))
}
